import { createEvent, createStore, sample } from 'effector';
import { persist } from 'effector-storage/local';

export interface CartItem {
  id: string;
  quantity: number;
}

const padId = (id: string): string => id.padStart(6, '0');

// Events
export const addItemToCart = createEvent<CartItem>();
export const removeItemFromCart = createEvent<string>();
export const clearCart = createEvent();
export const updateItemQuantity = createEvent<CartItem>();
export const loadCartFromStorage = createEvent();

// Store
export const $cartItems = createStore<CartItem[]>([])
  .on(addItemToCart, (state, item) => {
    const paddedId = padId(item.id);
    const existingItem = state.find((i) => i.id === paddedId);

    if (existingItem) {
      const newQuantity = existingItem.quantity + item.quantity;
      if (newQuantity <= 0) {
        return state.filter((i) => i.id !== paddedId);
      }
      return state.map((i) => (i.id === paddedId ? { ...i, quantity: newQuantity } : i));
    }

    return item.quantity > 0 ? [...state, { ...item, id: paddedId }] : state;
  })
  .on(removeItemFromCart, (state, id) => state.filter((item) => item.id !== padId(id)))
  .on(clearCart, () => [])
  .on(updateItemQuantity, (state, item) => {
    return state
      .map((i) => (i.id === padId(item.id) ? { ...i, quantity: item.quantity } : i))
      .filter((i) => i.quantity > 0);
  });

// Load initial state from storage
sample({
  clock: loadCartFromStorage,
  fn: (): CartItem[] => {
    const storedCart = localStorage.getItem('cart-storage');
    if (storedCart) {
      try {
        const parsedCart = JSON.parse(storedCart);
        return parsedCart || [];
      } catch (e) {
        console.error('Failed to parse cart from storage:', e);
        return [];
      }
    }
    return [];
  },
  target: $cartItems,
});

// Persist cart state
persist({
  store: $cartItems,
  key: 'cart-storage',
});

// Computed values
export const getItemQuantity = (id: string) => {
  const paddedId = padId(id);
  return $cartItems.map((items) => {
    const item = items.find((i) => i.id === paddedId);
    return item ? item.quantity : 0;
  });
};

// Initialize cart on app start
loadCartFromStorage();
